import { Visitor } from "./visitor";
import Avvvatars from "avvvatars-react";
import moment from "moment";
import "moment/locale/fr";
import { useState } from "react";
import VisitorDetailsModal from "./VisitorDetailsModal";
import VisitorEditModal from "./VisitorEditModal";
import VisitorDeleteModal from "./VisitorDeleteModal";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

interface Props {
  visitors: Visitor[];
  filieres: { label: string; id: string }[];
  onUpdate?: (visitor: Visitor) => void;
}

export const LEVEL_OF_INTEREST = [
  {
    label: "Pas Intéressé",
    value: "05%",
    color: "bg-danger",
  },
  {
    label: "Peu Intéressé",
    value: "25%",
    color: "bg-warning",
  },
  {
    label: "Intéressé",
    value: "75%",
    color: "bg-success",
  },
  {
    label: "Très Intéressé",
    value: "95%",
    color: "bg-success",
  },
  {
    label: "A Relancer",
    value: "45%",
    color: "bg-info",
  },
];

// bouton protégée pour modifier un visiteur
const ProtectedBtnEditVisitor = withPermission(
  (props: any) => (
    <button
      className="btn btn-icon btn-sm btn-soft-warning rounded-2 text-dark"
      onClick={() => props.handleShowEdit(props.visitor)}
      title="Modifier"
    >
      <i className="ti ti-edit"></i>
    </button>
  ),
  [buildPermission(Resource.VISITOR, Action.UPDATE)],
  "masquer",
);

// bouton protégée pour supprimer un visiteur
const ProtectedBtnDeleteVisitor = withPermission(
  (props: any) => (
    <button
      className="btn btn-icon btn-sm btn-soft-danger rounded-2 text-dark"
      onClick={() => props.handleShowDelete(props.visitor)}
      title="Supprimer"
    >
      <i className="ti ti-trash"></i>
    </button>
  ),
  [buildPermission(Resource.VISITOR, Action.DELETE)],
  "masquer",
);

const VisitorTable = ({ visitors, filieres, onUpdate }: Props) => {
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const handleShowDetails = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setShowDetails(true);
  };

  const handleShowEdit = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setShowEdit(true);
  };

  const handleShowDelete = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setShowDelete(true);
  };

  // Group visitors by date (using createdAt if available, otherwise fallback to today for demo)
  // Since Visitor interface doesn't have createdAt, I'll group by a mock date or just show the cards
  // In a real scenario, we would use v.createdAt
  const groupedVisitors = visitors.reduce((groups: any, visitor: any) => {
    const date = visitor.createdAt
      ? moment(visitor.createdAt).format("LL")
      : moment().format("LL");
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(visitor);
    return groups;
  }, {});
  const matchedLevelOfInterest = (label: string) => {
    const level = LEVEL_OF_INTEREST.find((item) => item.label === label);
    return level;
  };

  const matchedFiliere = (id: string) => {
    const filiere = filieres.find((item) => item.id === id);
    return filiere;
  };

  return (
    <div className="visitor-cards-container p-3">
      {Object.keys(groupedVisitors).length === 0 ? (
        <div className="text-center py-5">
          <i className="ti ti-users fs-48 text-muted mb-3 d-block"></i>
          <p className="text-muted">Aucun visiteur trouvé</p>
        </div>
      ) : (
        Object.entries(groupedVisitors).map(
          ([date, visitorsList]: [string, any]) => (
            <div key={date} className="mb-2">
              <div className="row g-3">
                {visitorsList.map((visitor: Visitor) => (
                  <div key={visitor.id} className="col-12 mb-3">
                    <div className="card border-0 shadow-sm h-100 visitor-card hover-translate-y mb-0">
                      <div className="card-body p-3">
                        <div className="row align-items-center">
                          {/* Card Header Section: Avatar & Contact */}
                          <div className="col-lg-3 col-md-4 mb-3 mb-md-0">
                            <div className="d-flex align-items-center">
                              <div className="avatar avatar-lg me-3 flex-shrink-0">
                                <Avvvatars
                                  value={`${visitor.firstname?.charAt(0) || ""}${visitor.lastname?.charAt(0) || ""}`}
                                  style="character"
                                  size={48}
                                />
                              </div>
                              <div className="overflow-hidden">
                                <h6 className="fw-bold mb-1 text-dark text-truncate">
                                  {visitor.firstname} {visitor.lastname}
                                </h6>
                                <p className="mb-0 text-primary fw-medium fs-13 d-flex align-items-center">
                                  <i className="ti ti-phone me-1"></i>
                                  {visitor.number || "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Info Items Section: Responsive Grid */}
                          <div className="col-lg-7 col-md-8">
                            <div className="row g-2 align-items-center">
                              <div className="col-sm-4">
                                <div className="info-item">
                                  <span className="text-muted fs-11 d-block mb-1 text-uppercase letter-spacing-1">
                                    Niveau souhaité
                                  </span>
                                  <span className="fw-semibold text-dark fs-13 d-flex align-items-center">
                                    <i className="ti ti-direction-arrows me-2 text-success"></i>
                                    {visitor.level_desired === "Autre"
                                      ? visitor.otherLevel_Of_Study
                                      : visitor.level_desired || "Non spécifié"}
                                  </span>
                                </div>
                              </div>

                              <div className="col-sm-4">
                                <div className="info-item">
                                  <span className="text-muted fs-11 d-block mb-1 text-uppercase letter-spacing-1">
                                    Filière souhaitée
                                  </span>
                                  <span className="fw-semibold text-dark fs-13 d-flex align-items-center">
                                    <i className="ti ti-books me-2 text-danger"></i>
                                    {matchedFiliere(visitor.id_filiere)
                                      ?.label || "Non spécifié"}
                                  </span>
                                </div>
                              </div>

                              <div className="col-sm-4">
                                <div className="info-item">
                                  <span className="text-muted fs-11 d-block mb-1 text-uppercase letter-spacing-1">
                                    Degré d'intérêt
                                  </span>
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="progress progress-xs flex-grow-1"
                                      style={{ height: "6px" }}
                                    >
                                      <div
                                        className={`progress-bar ${matchedLevelOfInterest(visitor.level_of_interest)?.color || "bg-info"}`}
                                        style={{
                                          width:
                                            matchedLevelOfInterest(
                                              visitor.level_of_interest,
                                            )?.value || "0%",
                                        }}
                                      ></div>
                                    </div>
                                    <span className="fs-12 fw-bold text-dark">
                                      {visitor.level_of_interest}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action & Status Section */}
                          <div className="col-lg-2 col-md-12 mt-3 mt-lg-0">
                            <div className="d-flex flex-row flex-lg-column align-items-center align-items-lg-end justify-content-between h-100 gap-2">
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-icon btn-sm btn-soft-primary rounded-2 text-dark"
                                  onClick={() => handleShowDetails(visitor)}
                                  title="Détails"
                                >
                                  <i className="ti ti-eye"></i>
                                </button>
                                <ProtectedBtnEditVisitor
                                  handleShowEdit={handleShowEdit}
                                  visitor={visitor}
                                />
                                <ProtectedBtnDeleteVisitor
                                  handleShowDelete={handleShowDelete}
                                  visitor={visitor}
                                />
                              </div>
                              <span
                                className={`badge ${visitor.registration_statut ? "bg-success" : "bg-warning"} text-white rounded-pill px-2 py-1 fs-10 text-uppercase`}
                              >
                                {visitor.registration_statut
                                  ? "Inscrit"
                                  : "En attente"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Card Footer: Date & Small Info */}
                        <div className="mt-2 pt-2 border-top d-flex align-items-center justify-content-between">
                          <small className="text-muted fs-15 d-flex align-items-center">
                            <i className="ti ti-calendar-event me-1"></i>
                            Enregistré le :{" "}
                            <span className="text-dark fw-medium ms-1">
                              {date}
                            </span>
                          </small>
                          {visitor.email && (
                            <small className="text-muted fs-15 d-none d-sm-block">
                              <i className="ti ti-mail me-1"></i>
                              {visitor.email}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ),
        )
      )}

      <VisitorDetailsModal
        show={showDetails}
        visitor={selectedVisitor}
        filiere={
          matchedFiliere(selectedVisitor?.id_filiere || "") || {
            label: "Non spécifié",
            id: "",
          }
        }
        onClose={() => setShowDetails(false)}
      />

      <VisitorEditModal
        show={showEdit}
        visitor={selectedVisitor}
        onClose={() => setShowEdit(false)}
        onSuccess={(updated: any) => {
          onUpdate?.(updated);
          setShowEdit(false);
        }}
        filieres={filieres}
      />

      <VisitorDeleteModal
        show={showDelete}
        visitor={selectedVisitor}
        onClose={() => setShowDelete(false)}
        onSuccess={() => {
          onUpdate?.(selectedVisitor!); // Trigger a refresh of the visitor list
          setShowDelete(false);
        }}
      />

      <style>{`
        .visitor-card {
          transition: all 0.3s ease;
          border: 2px solid #e9ecef !important;
        }
        .visitor-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
          border-color: var(--primary-color) !important;
        }
        .hover-translate-y {
          transition: transform 0.3s ease;
        }
        .btn-soft-primary { background-color: rgba(13, 110, 253, 0.1); color: #0d6efd; }
        .btn-soft-warning { background-color: rgba(255, 193, 7, 0.1); color: #ffc107; }
        .btn-soft-danger { background-color: rgba(220, 53, 69, 0.1); color: #dc3545; }
        .visitor-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 3fr;
          gap: 5px;
        }
        .info-item {
          padding: 5px;
        }
      `}</style>
    </div>
  );
};

export default VisitorTable;
